import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../api/axios";
import { REFUND_REQUESTERS } from "../../constants/roles";
import { userHasAnyRole } from "../../utils/leadershipRoles";
import RefundRequestModal from "../refunds/RefundRequestModal";
import BookingDocuments from "./BookingDocuments";


export default function BookingDetails({ user, onLogout }) {
  user = JSON.parse(localStorage.getItem("user"));
  const { id } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const canRequestRefund = userHasAnyRole(user, REFUND_REQUESTERS);
  const [refundRequests, setRefundRequests] = useState([]);
  const [refundPayment, setRefundPayment] = useState(null);
  const [refundSaving, setRefundSaving] = useState(false);

  // Refund requests already filed for this booking (staff only), to show "Refund requested" on the row.
  const loadRefundRequests = async () => {
    if (!canRequestRefund) return;
    try {
      const res = await api.get("/api/refund-requests", { params: { booking_id: id } });
      setRefundRequests(res.data?.data || []);
    } catch (error) {
      setRefundRequests([]);
    }
  };

  const submitRefundRequest = async (payload) => {
    setRefundSaving(true);
    try {
      await api.post("/api/refund-requests", payload);
      toast.success("Refund request sent to a Superadmin.");
      setRefundPayment(null);
      await loadRefundRequests();
      return true;
    } catch (error) {
      const errors = error?.response?.data?.errors;
      toast.error(
        (errors && Object.values(errors).flat()[0]) ||
          error?.response?.data?.message ||
          "Could not send the refund request."
      );
      return false;
    } finally {
      setRefundSaving(false);
    }
  };

  const pendingRefundFor = (paymentId) =>
    refundRequests.find((r) => r.payment_id === paymentId && r.status === "pending");

  useEffect(() => {
    loadRefundRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const fetchBookingDetails = async () => {
      try {
        const response = await api.get(`/api/bookings/${id}`);
        if (response.data && response.data.data) {
          console.log("Single Booking", response.data.data);

          setBooking(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching booking details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBookingDetails();
  }, [id]);

  const getPhotoUrl = (item) => {
    if (!item?.photos || !Array.isArray(item.photos) || item.photos.length === 0) {
      return null;
    }
    const photo = item.photos.find((p) => p.is_primary) || item.photos[0];
    if (photo.file_path.startsWith("http")) {
      return photo.file_path;
    }
    return `${api.defaults.baseURL}/${photo.file_path}`;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <>
        <div className="container py-5 text-center">
          <h3>Booking not found</h3>
          <button
            className="btn btn-secondary mt-3"
            onClick={() => navigate(-1)}
          >
            Go Back
          </button>
        </div>


      </>
    );
  }

  const { property, unit, status, payments } = booking;
  console.log("Booking history... property, unit, status, payments", unit);
  return (
    <>
      <div
        className="container-fluid"

      >
        <div className="card shadow-lg border-0 rounded-4 overflow-hidden">
          <div className="card-header bg-primary text-white p-4 d-flex justify-content-between align-items-center">
            <h2 className="mb-0 fw-bold">Booking Details #{booking.id}</h2>
            <button
              className="btn btn-light btn-sm"
              onClick={() => navigate(-1)}
            >
              &larr; Back
            </button>
          </div>

          <div className="card-body p-4">
            <div className="row g-4">
              {/* Left Column: Property & Unit Details */}
              <div className="col-lg-8">
                {/* Property Section */}
                <div className="card border-0 bg-light mb-4">
                  <div className="card-body">
                    <h4 className="text-primary border-bottom pb-2 mb-3">
                      Property Information
                    </h4>
                    <div className="row">
                      <div className="col-md-4 mb-3">
                        <div
                          className="border rounded overflow-hidden bg-white d-flex align-items-center justify-content-center"
                          style={{ height: "200px" }}
                        >
                          {getPhotoUrl(property) ? (
                            <img
                              src={getPhotoUrl(property)}
                              alt={property?.name}
                              className="img-fluid"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <span className="text-muted">No Image</span>
                          )}
                        </div>
                      </div>
                      <div className="col-md-8">
                        <h5 className="fw-bold">{property?.name}</h5>
                        <p className="text-muted mb-2">
                          <i className="bi bi-geo-alt-fill text-danger"></i>{" "}
                          {property?.address}
                        </p>

                        <div className="row mt-3">
                          <div className="col-6 mb-2">
                            <small className="text-muted text-uppercase d-block">
                              Type
                            </small>
                            <span className="fw-semibold">
                              {property?.type?.name || "—"}
                            </span>
                          </div>
                          <div className="col-6 mb-2">
                            <small className="text-muted text-uppercase d-block">
                              Location
                            </small>
                            <span className="fw-semibold">
                              {property?.location?.area || "—"}
                              {property?.location?.city
                                ? `, ${property.location.city}`
                                : ""}
                            </span>
                          </div>
                          <div className="col-6 mb-2">
                            <small className="text-muted text-uppercase d-block">
                              Total Floors
                            </small>
                            <span className="fw-semibold">
                              {property?.total_floors || "—"}
                            </span>
                          </div>
                          <div className="col-6 mb-2">
                            <small className="text-muted text-uppercase d-block">
                              Area
                            </small>
                            <span className="fw-semibold">
                              {property?.area_sq_ft
                                ? `${property.area_sq_ft} sq ft`
                                : "—"}
                            </span>
                          </div>
                        </div>

                        {(property?.owner || property?.manager) && (
                          <div className="mt-3 pt-2 border-top">
                            {property?.owner && (
                              <div className="mb-1">
                                <small className="text-muted">Owner: </small>
                                <span className="fw-semibold">
                                  {property.owner.name}
                                </span>
                                <small className="text-muted ms-2">
                                  ({property.owner.mobile_number})
                                </small>
                              </div>
                            )}
                            {property?.manager && (
                              <div>
                                <small className="text-muted">Manager: </small>
                                <span className="fw-semibold">
                                  {property.manager.name}
                                </span>
                                <small className="text-muted ms-2">
                                  ({property.manager.mobile_number})
                                </small>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Unit Section */}
                <div className="card border-0 bg-light">
                  <div className="card-body">
                    <h4 className="text-primary border-bottom pb-2 mb-3">
                      Unit Information
                    </h4>
                    <div className="row">
                      <div className="col-md-4 mb-3">
                        <div
                          className="border rounded overflow-hidden bg-white d-flex align-items-center justify-content-center"
                          style={{ height: "200px" }}
                        >
                          {getPhotoUrl(unit) ? (
                            <img
                              src={getPhotoUrl(unit)}
                              alt={unit?.unit_number}
                              className="img-fluid"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <span className="text-muted">No Image</span>
                          )}
                        </div>
                      </div>
                      <div className="col-md-8">
                        <h5 className="fw-bold">Unit {unit?.unit_number}</h5>
                        <div className="mb-3">
                          <span
                            className={`badge ${unit?.status?.status_type === "available"
                                ? "bg-success"
                                : "bg-secondary"
                              }`}
                          >
                            {unit?.status?.status_type || unit?.status_id}
                          </span>
                        </div>

                        <div className="row">
                          <div className="col-6 mb-2">
                            <small className="text-muted text-uppercase d-block">
                              Floor
                            </small>
                            <span className="fw-semibold">
                              {unit?.floor || "—"}
                            </span>
                          </div>
                          <div className="col-6 mb-2">
                            <small className="text-muted text-uppercase d-block">
                              Type
                            </small>
                            <span className="fw-semibold">
                              {unit?.type?.name || "—"}
                            </span>
                          </div>
                          <div className="col-6 mb-2">
                            <small className="text-muted text-uppercase d-block">
                              Bedrooms
                            </small>
                            <span className="fw-semibold">
                              {unit?.bedroom || "—"}
                            </span>
                          </div>
                          <div className="col-6 mb-2">
                            <small className="text-muted text-uppercase d-block">
                              Bathrooms
                            </small>
                            <span className="fw-semibold">
                              {unit?.bathroom || "—"}
                            </span>
                          </div>
                          <div className="col-12 mb-2">
                            <small className="text-muted text-uppercase d-block">
                              Area
                            </small>
                            <span className="fw-semibold">
                              {unit?.measurement?.length > 0
                                ? `${unit.measurement[0].area_sq_ft} ${unit.measurement[0].unit_of_measure}`
                                : unit?.measurement?.area_sq_ft
                                  ? `${unit.measurement.area_sq_ft} ${unit.measurement.unit_of_measure}`
                                  : "N/A"}
                            </span>
                          </div>
                        </div>

                        {unit?.facilities && (
                          <div className="mt-2">
                            <small className="text-muted text-uppercase d-block">
                              Facilities
                            </small>
                            <p className="small mb-0">
                              {Array.isArray(unit.facilities)
                                ? unit.facilities.join(", ")
                                : typeof unit.facilities === "object" &&
                                  unit.facilities !== null
                                  ? Object.values(unit.facilities).join(", ")
                                  : unit.facilities}
                            </p>
                          </div>
                        )}

                        {(unit?.owner || unit?.manager) && (
                          <div className="mt-3 pt-2 border-top">
                            {unit?.owner && (
                              <div className="mb-1">
                                <small className="text-muted">
                                  Unit Owner:{" "}
                                </small>
                                <span className="fw-semibold">
                                  {unit.owner.name}
                                </span>
                              </div>
                            )}
                            {unit?.manager && (
                              <div>
                                <small className="text-muted">
                                  Unit Manager:{" "}
                                </small>
                                <span className="fw-semibold">
                                  {unit.manager.name}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column: Booking Status & Financials */}
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm mb-4">
                  <div className="card-body">
                    <h4 className="text-primary border-bottom pb-2 mb-3">
                      Booking Status
                    </h4>

                    <div className="mb-3">
                      <label className="text-muted small text-uppercase">
                        Status
                      </label>
                      <div className="mt-1">
                        <span
                          className={`badge fs-6 px-3 py-2 ${status?.status_type === "approved"
                              ? "bg-success"
                              : status?.status_type === "pending"
                                ? "bg-warning text-dark"
                                : "bg-secondary"
                            }`}
                        >
                          {status?.name || status?.status_type}
                        </span>
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="text-muted small text-uppercase">
                        Booking Date
                      </label>
                      <p className="fs-5 fw-semibold">
                        {new Date(booking.created_at).toLocaleDateString()}
                      </p>
                    </div>

                    <div className="mb-3">
                      <label className="text-muted small text-uppercase">
                        Check in before
                      </label>
                      <p className="fs-5 fw-semibold">
                        {booking.check_out_date
                          ? new Date(
                            booking.check_out_date
                          ).toLocaleDateString()
                          : "N/A"}
                      </p>
                    </div>
                  </div>
                </div>

                <BookingDocuments bookingId={booking.id} />

                <div className="card border-0 shadow-sm mb-4">
                  <div className="card-body">
                    <h4 className="text-primary border-bottom pb-2 mb-3">
                      Payment Details
                    </h4>

                    <div className="card bg-light border-0 rounded-3 p-3 mb-3">
                      <div className="d-flex justify-content-between mb-2">
                        <span>Total Amount:</span>
                        <span className="fw-bold">
                          ৳ {Number(booking.total_amount).toLocaleString()}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between mb-2 text-success">
                        <span>Paid Amount:</span>
                        <span className="fw-bold">
                          ৳ {Number(booking.paid_amount).toLocaleString()}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between text-danger pt-2 border-top border-secondary">
                        <span>Due Amount:</span>
                        <span className="fw-bold">
                          ৳ {Number(booking.due_amount).toLocaleString()}
                        </span>
                      </div>
                    </div>

                    {Number(booking.due_amount) > 0 && (
                      <button
                        className="btn btn-success w-100 py-2 fw-bold shadow-sm"
                        onClick={() =>
                          navigate(
                            `/properties_available_booking/${booking.id}`,
                            { state: { bookingDetails: booking } }
                          )
                        }
                      >
                        Pay Now <i className="bi bi-credit-card ms-2"></i>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Payment History */}
            <div className="row mt-4">
              <div className="col-12">
                <h4 className="text-primary border-bottom pb-2 mb-3">
                  Payment History
                </h4>
                {payments && payments.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-hover align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>Date</th>
                          <th>Transaction ID</th>
                          <th>Unit</th>
                          <th>Method</th>
                          <th className="text-end">Amount</th>
                          <th className="text-center">Status</th>
                          {canRequestRefund && <th className="text-center">Refund</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {payments.map((pay) => (
                          <tr key={pay.id}>
                            <td>
                              {new Date(pay.created_at).toLocaleDateString()}
                            </td>
                            <td>{pay.id || "-"}</td>
                            <td>{unit.unit_number}</td>
                            <td className="text-capitalize">
                              {pay.payment_method}
                            </td>
                            <td className="text-end fw-bold">
                              ৳ {Number(pay.amount).toLocaleString()}
                            </td>

                            <td className="text-center">
                              <span
                                className={`badge ${pay.payment_status === "success"
                                    ? "bg-success"
                                    : "bg-warning text-dark"
                                  }`}
                              >
                                {pay.payment_status}
                              </span>
                            </td>
                            {canRequestRefund && (
                              <td className="text-center">
                                {pay.refunded_at ? (
                                  <span className="badge bg-secondary">Refunded</span>
                                ) : pendingRefundFor(pay.id) ? (
                                  <span className="badge bg-warning text-dark">Refund requested</span>
                                ) : pay.payment_status === "success" ? (
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm"
                                    onClick={() => setRefundPayment(pay)}
                                  >
                                    Request refund
                                  </button>
                                ) : null}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-muted text-center py-3 bg-light rounded">
                    No payment history found.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <RefundRequestModal
        show={!!refundPayment}
        payment={refundPayment}
        saving={refundSaving}
        onHide={() => setRefundPayment(null)}
        onSubmit={submitRefundRequest}
      />
    </>
  );
}
